import { SaveOutlined } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@thedealersconcierge/components';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCrop, { Crop, PercentCrop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { toast } from 'react-toastify';
import { getScanbotSdk } from '~/lib/scanbot-client';
import { BaseLayout } from '~/pages/scanner/_components/ScreenBaseLayout';
import { Page, ScreenModes } from './ScreenController';

export default function CropScreen({
  page,
  setScreenMode,
  setPage
}: {
  page: Page;
  setScreenMode: (mode: ScreenModes) => void;
  setPage: (page: Page) => void;
}) {
  const { t } = useTranslation();
  const imageRef = useRef<HTMLImageElement>(null);

  // Initial crop state
  // This will set the initial bounding box for the crop selector
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    x: 0,
    y: 0,
    width: 100,
    height: 100
  });

  // Crop result in Pixel
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

  const { data: url } = useQuery({
    queryKey: ['cropscreen-image-target', page.id],
    queryFn: async () => {
      const sdk = await getScanbotSdk();
      const thumbnailJpg = page.capture
        ? await sdk.imageToJpeg(page.capture)
        : undefined;

      const thumbnailBlob = thumbnailJpg
        ? new Blob([thumbnailJpg], { type: 'image/jpeg' })
        : null;
      return thumbnailBlob ? URL.createObjectURL(thumbnailBlob) : undefined;
    }
  });

  const handleSave = async () => {
    if (!completedCrop || !page.capture || !imageRef.current) {
      toast.error(t('Something went wrong when trying to save, try again'));
      return;
    }

    // dimensions of rendered image
    const img = imageRef.current;
    const displayW = img.clientWidth;
    const displayH = img.clientHeight;

    // pixelCrop is relative to displayed image
    const { x, y, width, height } = completedCrop;

    // normalize it
    const topLeft = { x: x / displayW, y: y / displayH };
    const topRight = { x: (x + width) / displayW, y: y / displayH };
    const bottomRight = {
      x: (x + width) / displayW,
      y: (y + height) / displayH
    };
    const bottomLeft = { x: x / displayW, y: (y + height) / displayH };

    try {
      const sdk = await getScanbotSdk();
      const cropped = await sdk.imageCrop(page.capture, [
        topLeft,
        topRight,
        bottomRight,
        bottomLeft
      ]);
      setPage({ id: page.id, capture: cropped });
      setScreenMode('REVIEW_SINGULAR');
    } catch (err) {
      console.error(err);
      toast.error(t('Failed saving cropped image'));
    }
  };

  return (
    <BaseLayout>
      {/* Top Bar */}
      <BaseLayout.Top>
        <div className="flex justify-between items-center w-full px-spacing-04 py-spacing-02 border-b">
          <Button
            variant="GHOST"
            label="Cancel"
            size="SMALL"
            onClick={() => setScreenMode('REVIEW_SINGULAR')}
          />
          <Button
            variant="GHOST"
            label={t('Save')}
            size="SMALL"
            onClick={handleSave}
            disabled={!completedCrop}
          />
        </div>
      </BaseLayout.Top>

      {/* Main Content: ReactCrop + Preview Canvas */}
      <BaseLayout.Content>
        <div className="flex flex-col items-center justify-center w-full h-full p-4 bg-gray-100 overflow-y-scroll">
          {url ? (
            <ReactCrop
              crop={crop}
              onChange={(newCrop) => setCrop(newCrop)}
              onComplete={(
                pixelCreop: PixelCrop,
                _percentageCrop: PercentCrop
              ) => setCompletedCrop(pixelCreop)}
              keepSelection
            >
              <img
                ref={imageRef}
                src={url}
                alt="To Crop"
                style={{
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  display: 'block',
                  margin: '0 auto'
                }}
              />
            </ReactCrop>
          ) : (
            <p>{t('No image available for cropping')}</p>
          )}
        </div>
      </BaseLayout.Content>

      {/* Footer with Save Button */}
      <BaseLayout.Footer>
        <div className="p-2 border-t flex justify-around z-20">
          <div
            className={classNames(
              'flex flex-col items-center text-primary-brand cursor-pointer px-spacing-03 py-spacing-02 gap-spacing-01',
              { 'text-gray-200': !completedCrop }
            )}
            onClick={() => {
              if (!imageRef.current || !completedCrop) return;
              void handleSave();
            }}
          >
            <SaveOutlined />
            <p className="label-02 font-normal">{t('Save')}</p>
          </div>
        </div>
      </BaseLayout.Footer>
    </BaseLayout>
  );
}
